import React, { Suspense, useEffect, useState } from "react";
import { connect } from "react-redux";
import { ToastTypes, withToast } from "../../../contexts/toastr.context";
import { Button, Loader, TextInput } from "../../../_components";
import { ActionModelTrigger } from "../../../_components/action-modal.component";
import { Checkbox } from "../../../_components/form-controls/checkbox.component";
import { InputSwitch } from "../../../_components/form-controls/input-switch.component";
import { ValidationErrorDiv } from "../../../_components/validation-error.component";
import { CommunityService } from "../../../_service";
import { setCommunity } from "../../../_store/_actions/community.actions";
import { useAppService } from "../../../hooks/use-app-service";
const CommunityConfigurable = React.lazy(() => import("../../../_components/atoms/community-configurable.component"));

const USER_ACTIVITY_TYPE_INVERSE = {
  "post-like": "Liking a post",
  "post-comment": "Creating a comment",
  "comment-reply": "Creating a reply",
  "post-liked": "Getting like on a post",
  "post-commented": "Getting comment on a post",
  "comment-replied": "Getting reply on a comment",
  // 'follow': 'follow',
  "post-create": "Creating a post",
  // 'group:user-joined': 'User joins a group',
  // 'group:user-requested': 'User requested',
  // 'group:request-accepted': 'joinRequestAccepted',
  // 'group:going-live': 'goingLiveInGroup',
  // 'going-live': 'goingLive',
};

function KarmaPointsTabComponent({
  addToast,
  user,
  community,
  updateCommunity,
}) {
  const { analyticsService } = useAppService();

  const [isLoadingKarmaPoints, setIsLoadingKarmaPoints] = useState(false);
  const [karmaPoints, setKarmaPoints] = useState([]);
  const [karmaPointsDefault, setKarmaPointsDefault] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [
    isLoadingKarmaPointCheckboxLoading,
    setIsLoadingKarmaPointCheckboxLoading,
  ] = useState(false);

  const communityId = community?.id;

  const updateKarmaPoint = (karmaPoint) => {
    const index = karmaPoints.findIndex((kP) => kP._id === karmaPoint._id);
    const newKarmaPoints = [...karmaPoints];
    newKarmaPoints.splice(index, 1, {
      ...karmaPoint,
      pointsEarned: karmaPoint.isEnabled ? karmaPoint.pointsEarned : 0,
    });
    setKarmaPoints(newKarmaPoints);
  };

  const resetKarmaPoints = () => {
    setKarmaPoints(karmaPointsDefault);
  };

  const submitKarmaPointChanges = () => {
    setValidationErrors({});
    CommunityService.updateCommunityKarmaPoints(communityId, karmaPoints)
      .then(({ karmaPoints, message }) => {
        setKarmaPoints(karmaPoints);
        setKarmaPointsDefault(karmaPoints);
        addToast(message);
      })
      .catch((error) => {
        console.log(error);
        setValidationErrors(error.response?.data?.errors);
        addToast("Could not update karma points!", "", ToastTypes.danger);
      })
      .finally(() => {
        setIsLoading(false);
        analyticsService.track("update-points")
      });
  };

  useEffect(() => {
    setIsLoadingKarmaPoints(true);
    CommunityService.getCommunityKarmaPoints(communityId)
      .then(({ karmaPoints, message }) => {
        setKarmaPoints(karmaPoints);
        setKarmaPointsDefault(karmaPoints);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoadingKarmaPoints(false);
      });
  }, [communityId]);

  const updateConfig = (value) => {
    if (isLoadingKarmaPointCheckboxLoading) return;

    setIsLoadingKarmaPointCheckboxLoading(true);
    CommunityService.updateConfigV2(
      user,
      community.id,
      "allowAdminsToAssignCustomKarmaPoints",
      value
    )
      .then(({ community }) => {
        // addToast("Updated", "", ToastTypes.success);
        updateCommunity(community);
        setIsLoadingKarmaPointCheckboxLoading(false);
      })
      .catch((err) => {
        addToast("Failed to do so.", "", ToastTypes.danger);
        console.log(err);
        setIsLoadingKarmaPointCheckboxLoading(false);
      });
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between border theme-border-default rounded-lg theme-bg-default p-4 w-full flex-grow focus:outline-none resize-none mb-10">
        Allow admins to assign/un-assign custom points to users.
        <InputSwitch
          selected={
            community?.configuration?.allowAdminsToAssignCustomKarmaPoints
          }
          className="mx-2"
          onClick={(e) => {
            updateConfig(
              !community?.configuration?.allowAdminsToAssignCustomKarmaPoints
            );
          }}
          alt
        />
      </div>
      {/* base point */}
      <div className="flex items-center justify-between border theme-border-default rounded-lg theme-bg-default p-4 w-full flex-grow focus:outline-none resize-none mb-10">
        <Suspense fallback={<></>}>
          <CommunityConfigurable
            community={community}
            configurableKey="COMMUNITY_BASE_POINT_KEY"
            title="Base point for user to begin with."
            subTitle="Base points will be updated for all the users, any changes will reflect for all the users."
          />
        </Suspense>
      </div>
      {/* point configuration */}
      <div className="theme-text-heading-1 font-semibold text-lg">
        Allocate Point for Interaction
      </div>
      {isLoadingKarmaPoints ? (
        <Loader />
      ) : (
        karmaPoints.map((karmaPoint, index) => (
          <>
            <KarmaPoint
              key={karmaPoint.id}
              karmaPoint={karmaPoint}
              updateKarmaPoint={updateKarmaPoint}
            />
            <ValidationErrorDiv
              errors={validationErrors}
              errorKey={`karmaPoints.${index}.pointsEarned`}
              keySubstitute="Points earned"
            />
          </>
        ))
      )}
      <div className="py-5 mx-10 flex justify-between">
        <ActionModelTrigger
          header="Are you sure to reset your changes?"
          onSubmit={(close) => {
            resetKarmaPoints();
            close();
          }}>
          <Button label="Cancel" flat className="w-24" />
        </ActionModelTrigger>
        <Button
          label="Save"
          className="w-24"
          onClick={submitKarmaPointChanges}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function KarmaPoint({ karmaPoint, updateKarmaPoint }) {
  const label = USER_ACTIVITY_TYPE_INVERSE[karmaPoint.activityType];

  if (!label) return <></>;

  return (
    <div className="px-2 flex items-center justify-between">
      <div>
        <Checkbox
          selected={karmaPoint.isEnabled}
          onClick={(e) => {
            updateKarmaPoint({
              ...karmaPoint,
              isEnabled: !karmaPoint.isEnabled,
            });
          }}>
          <div className="pl-2">{label}</div>
        </Checkbox>
      </div>
      <div className="w-24 flex items-center mr-10">
        <TextInput
          className="text-center"
          defaultValue={karmaPoint.pointsEarned ?? 0}
          onChange={(value) => {
            updateKarmaPoint({ ...karmaPoint, pointsEarned: value });
          }}
        />
        <span className="text-sm pl-2">point</span>
      </div>
    </div>
  );
}

const stp = (state) => ({
  user: state.auth,
  community: state.community,
});

const dtp = {
  updateCommunity: setCommunity,
};

const KarmaPointsTab = withToast(connect(stp, dtp)(KarmaPointsTabComponent));

export default KarmaPointsTab;
